/**
 * The Long Way Up: one scroll from the dome to the whole galaxy.
 * Progress runs from 0 (just above the dome) to 1 (a pause among pure stars).
 */

export const LIGHT_YEAR_KM = 9.4607e12
export const AU_KM = 149597870.7
export const LIGHT_MINUTE_KM = 17987547.48

/** Distance from home in kilometres at each point of the journey. */
const DISTANCE_KEYS: [number, number][] = [
    [0, 0.002],
    [0.02, 0.05],
    [0.05, 0.6],
    [0.08, 3],
    [0.1, 12],
    [0.13, 100],
    [0.16, 408],
    [0.24, 35786],
    [0.33, 384400],
    [0.42, 78340000],
    [0.48, 628700000],
    [0.535, 1275000000],
    [0.585, 2723000000],
    [0.62, 4325000000],
    [0.67, 121 * AU_KM],
    [0.725, 1.5 * LIGHT_YEAR_KM],
    [0.78, 4.37 * LIGHT_YEAR_KM],
    [0.88, 26000 * LIGHT_YEAR_KM],
    [0.95, 150000 * LIGHT_YEAR_KM],
    [1, 150000 * LIGHT_YEAR_KM]
]

export const distanceAt = (progress: number): number => {
    const p = Math.min(1, Math.max(0, progress))

    for (let i = 0; i < DISTANCE_KEYS.length - 1; i++) {
        const [a0, d0] = DISTANCE_KEYS[i]
        const [a1, d1] = DISTANCE_KEYS[i + 1]

        if (p <= a1) {
            const t = a1 === a0 ? 1 : (p - a0) / (a1 - a0)
            const eased = t * t * (3 - 2 * t)

            return d0 * Math.pow(d1 / d0, eased)
        }
    }

    return DISTANCE_KEYS[DISTANCE_KEYS.length - 1][1]
}

export type DistanceReading = {
    value: string
    unit: string
    /** Position along a logarithmic ruler, 0 at one metre, 1 at the far edge of the galaxy. */
    ruler: number
}

const group = (value: number, digits = 0) => value.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits })

export const readDistance = (km: number): DistanceReading => {
    const ruler = Math.min(1, Math.max(0, (Math.log10(Math.max(km, 0.001)) + 3) / 21.2))

    if (km < 0.01) return { value: '0', unit: 'km', ruler }
    if (km < 10) return { value: group(km, km < 1 ? 2 : 1), unit: 'km', ruler }
    if (km < 1e7) return { value: group(Math.round(km)), unit: 'km', ruler }

    const au = km / AU_KM

    if (au < 12) return { value: group(au, 2), unit: 'AU', ruler }

    const lightMinutes = km / LIGHT_MINUTE_KM

    if (km < 0.1 * LIGHT_YEAR_KM) return { value: group(Math.round(lightMinutes)), unit: 'light-minutes', ruler }

    const lightYears = km / LIGHT_YEAR_KM

    if (lightYears < 10) return { value: group(lightYears, 2), unit: 'light-years', ruler }
    if (lightYears < 1000) return { value: group(Math.round(lightYears)), unit: 'light-years', ruler }

    const thousands = lightYears / 1000

    return { value: group(thousands, thousands < 10 ? 1 : 0), unit: 'thousand light-years', ruler }
}

export type Milestone = {
    start: number
    end: number
    distance: string
    line: string
}

export const MILESTONES: Milestone[] = [
    { start: 0.004, end: 0.05, distance: '420 m above the sea', line: 'The dome, the hill and the dark. Everything you can see from here is still above you.' },
    { start: 0.062, end: 0.1, distance: '3 km', line: 'The village lights shrink to embers. Already the air is thinning.' },
    { start: 0.115, end: 0.15, distance: '100 km', line: 'The Kármán line. By most definitions, this is where space begins.' },
    { start: 0.155, end: 0.192, distance: '408 km', line: 'The International Space Station circles at this height every ninety-two minutes.' },
    { start: 0.208, end: 0.255, distance: '35,786 km', line: 'Geostationary orbit, where weather satellites hang still above one spot on Earth.' },
    { start: 0.31, end: 0.36, distance: '384,400 km', line: 'Light from here takes 1.3 seconds to reach you.' },
    { start: 0.405, end: 0.44, distance: '0.52 AU', line: 'Mars at its closest. Its light set out a little over four minutes ago.' },
    { start: 0.462, end: 0.5, distance: '4.20 AU', line: 'Jupiter, eleven Earths wide. Sunlight takes forty-three minutes to arrive.' },
    { start: 0.518, end: 0.556, distance: '8.52 AU', line: 'Saturn’s rings are 270,000 kilometres across and, in places, only ten metres thick.' },
    { start: 0.568, end: 0.6, distance: '151 light-minutes', line: 'Uranus rolls around the Sun on its side.' },
    { start: 0.603, end: 0.636, distance: '240 light-minutes', line: 'Neptune. Noon here is about as bright as a dull evening on Earth.' },
    { start: 0.652, end: 0.69, distance: '1,006 light-minutes', line: 'Voyager 1 crossed the edge of the Sun’s wind here in 2012, and kept going.' },
    { start: 0.707, end: 0.745, distance: '1.5 light-years', line: 'The far edge of the Oort cloud, a shell of ice that no telescope has ever seen.' },
    { start: 0.762, end: 0.802, distance: '4.37 light-years', line: 'Alpha Centauri. The light you see from it tonight left in 2022.' },
    { start: 0.855, end: 0.9, distance: '26 thousand light-years', line: 'The centre of our galaxy, hidden behind dust in Sagittarius.' },
    { start: 0.912, end: 0.952, distance: '150 thousand light-years', line: 'Almost everything you have ever seen in the night sky is inside this.' }
]

/** Hero section length: one screen of dome, then the ascent. */
export const ASCENT_SCREENS = 8.5
export const HERO_HOLD = 0.035
export const HERO_RISE = 0.075

export type SectionPhase = {
    /** 0 to 1 while the camera rises from the floor to the slit. */
    rise: number
    /** Ascent progress, 0 to 1. Negative before the ascent begins. */
    ascent: number
}

/** Splits the pinned section's scroll progress into the dome rise and the ascent. */
export const phaseFor = (sectionProgress: number): SectionPhase => {
    const rise = Math.min(1, Math.max(0, (sectionProgress - HERO_HOLD) / (HERO_RISE - HERO_HOLD)))
    const ascent = (sectionProgress - HERO_RISE) / (1 - HERO_RISE)

    return { rise, ascent }
}

export const fadeWindow = (progress: number, start: number, end: number, edge = 0.012): number => {
    if (progress <= start - edge || progress >= end + edge) return 0
    if (progress < start + edge) return (progress - (start - edge)) / (2 * edge)
    if (progress > end - edge) return ((end + edge) - progress) / (2 * edge)

    return 1
}
